// Pure mappers + block types for the chat render card. No React / CopilotKit
// imports, so this is unit-testable in a plain Node environment (see
// renderMappers.test.ts) and importable by both RenderBlocks and ThreadPage.

// ---- Block types (mirror the backend `frontend_widget_render` schema) -------
// `badges` items may be plain strings (from the render tool) or {label,color}
// objects (from the auto-render mappers, which colour-code by category/type).
export type BadgeItem = string | { label: string; color?: string }
interface HeadingBlock {
    type: 'heading'
    text?: string
}
interface TextBlock {
    type: 'text'
    text?: string
}
interface BadgesBlock {
    type: 'badges'
    items?: BadgeItem[]
}
export interface TableRow {
    label?: string
    value?: string
}
interface TableBlock {
    type: 'table'
    rows?: TableRow[]
}
interface ListBlock {
    type: 'list'
    items?: string[]
}
export type Block =
    | HeadingBlock
    | TextBlock
    | BadgesBlock
    | TableBlock
    | ListBlock

// ---- mappers: side-effect tool args → blocks --------------------------------
// flash-lite reliably calls the side-effect tools (add_strategy_recommendation,
// save_transaction_audience, …) but skips the display-only render tool, so we
// render those calls as cards by mapping their rich args into blocks.

const humanize = (s: unknown): string => {
    if (typeof s !== 'string' && typeof s !== 'number') return ''
    return String(s)
        .replace(/_/g, ' ')
        .toLowerCase()
        .replace(/\b\w/g, (c) => c.toUpperCase())
}

const str = (v: unknown): string | undefined =>
    typeof v === 'string' && v.trim() ? v : undefined

export const formatReach = (n: unknown): string | undefined => {
    // 0 means "not computed yet" (the backend now defaults audience_size to 0
    // instead of null), so treat it like a missing size and show nothing.
    if (typeof n !== 'number' || !isFinite(n) || n <= 0) return undefined
    // Always show 3 significant figures. Round to 3 sig figs FIRST so the unit
    // and decimal count are chosen from the rounded magnitude — otherwise a
    // value like 999,999 would bucket as ~1000k instead of promoting to ~1.00M.
    const rounded = Number(n.toPrecision(3))
    // 3 sig figs within a [1,1000) band: 2 decimals < 10, 1 decimal < 100, else 0.
    const fmt = (x: number): string => x.toFixed(x >= 100 ? 0 : x >= 10 ? 1 : 2)
    const units: [number, string][] = [
        [1_000_000_000, 'B'],
        [1_000_000, 'M'],
        [1_000, 'k'],
    ]
    for (const [value, suffix] of units) {
        if (rounded >= value) return `~${fmt(rounded / value)}${suffix}`
    }
    return `~${fmt(rounded)}`
}

interface FieldValue {
    field?: string
    values?: unknown[]
}

// Recursively collect every {field, values} leaf reachable through must /
// should / must_not — covers both TransactionDefinition (flat FieldValues) and
// UserTransactionBehavior (FieldValues nested under TransactionDefinitions).
function collectFieldValues(
    node: unknown,
    out: FieldValue[] = []
): FieldValue[] {
    if (!node || typeof node !== 'object') return out
    if (Array.isArray(node)) {
        node.forEach((n) => collectFieldValues(n, out))
        return out
    }
    const obj = node as Record<string, unknown>
    if (typeof obj.field === 'string' && Array.isArray(obj.values)) {
        out.push(obj as FieldValue)
        return out
    }
    for (const k of ['must', 'should', 'must_not']) {
        if (obj[k]) collectFieldValues(obj[k], out)
    }
    return out
}

const fieldValuesToRows = (fvs: FieldValue[]): TableRow[] =>
    fvs
        .filter((fv) => fv.field)
        .map((fv) => ({
            label: humanize(fv.field),
            value: (fv.values ?? [])
                .map((v) => humanize(v))
                .filter(Boolean)
                .join(', '),
        }))
        .filter((r) => r.value)

// How many field/value filter conditions define a segment: every {field, values}
// leaf across its transaction behaviors and attribute definition. Works for all
// config types (transaction, user-attribute, crossed) since each reduces to
// these two collections. Reads defensively (config may be partial/raw).
export function countFilters(config: unknown): number {
    if (!config || typeof config !== 'object') return 0
    const c = config as Record<string, unknown>
    return (
        collectFieldValues(c.users_with_transactions).length +
        collectFieldValues(c.users_with_attributes).length
    )
}

export const datasetLabel = (
    id: unknown,
    names: Record<string, string>
): string => {
    const key = typeof id === 'string' ? id : ''
    return key ? (names[key] ?? humanize(key)) : ''
}

const compact = (blocks: (Block | null)[]): { blocks: Block[] } => ({
    blocks: blocks.filter((b): b is Block => b !== null),
})

// A built/saved segment (TransactionAudienceConfiguration,
// UserAttributeAudienceConfiguration, or CrossedAudienceConfiguration).
export function audienceConfigToBlocks(
    config: Record<string, unknown>,
    names: Record<string, string>
): { blocks: Block[] } {
    const c = config ?? {}
    const datasetType = str(c.dataset_type)
    const typeBadge: { label: string; color?: string } | null =
        datasetType === 'transaction'
            ? { label: 'Transaction', color: 'sage' }
            : datasetType === 'user_attribute'
              ? { label: 'User Attribute', color: 'coral' }
              : datasetType === 'crossed'
                ? { label: 'Crossed', color: 'plum' }
                : null
    // A crossed segment spans two datasets, so badge both: `dataset_id` (the
    // transaction side) and `users_with_attributes_dataset_id` (the user-attribute
    // side). Every other type has a single dataset.
    const datasetIds =
        datasetType === 'crossed'
            ? [c.dataset_id, c.users_with_attributes_dataset_id]
            : [c.dataset_id]
    const dsBadges = datasetIds
        .map((id) => datasetLabel(id, names))
        .filter(Boolean)
    const reach = formatReach(c.audience_size)
    const badges: BadgeItem[] = [
        ...(typeBadge ? [typeBadge] : []),
        ...dsBadges,
        ...(reach ? [reach] : []),
    ]
    const rows = fieldValuesToRows([
        ...collectFieldValues(c.users_with_transactions),
        ...collectFieldValues(c.users_with_attributes),
    ])
    return compact([
        str(c.name) ? { type: 'heading', text: str(c.name) } : null,
        badges.length ? { type: 'badges', items: badges } : null,
        str(c.description) ? { type: 'text', text: str(c.description) } : null,
        rows.length ? { type: 'table', rows } : null,
    ])
}

// edit_new_boolean_logic_audience passes the filters as JSON strings. The size
// isn't an argument of that tool (it's computed later by combine_segments_tool),
// so callers thread it in via `audienceSize` once it's known on agent state.
export function booleanEditToBlocks(
    args: Record<string, unknown>,
    names: Record<string, string>,
    audienceSize?: number
): { blocks: Block[] } {
    const parse = (v: unknown): unknown => {
        if (typeof v !== 'string' || !v.trim()) return []
        try {
            return JSON.parse(v)
        } catch {
            return []
        }
    }
    const rows = fieldValuesToRows([
        ...collectFieldValues(parse(args.must_json)),
        ...collectFieldValues(parse(args.should_json)),
        ...collectFieldValues(parse(args.must_not_json)),
    ])
    const ds = datasetLabel(args.dataset_id, names)
    const reach = formatReach(audienceSize)
    const badges: BadgeItem[] = [
        { label: 'User Attribute', color: 'coral' },
        ...(ds ? [ds] : []),
        ...(reach ? [reach] : []),
    ]
    return compact([
        str(args.name) ? { type: 'heading', text: str(args.name) } : null,
        { type: 'badges', items: badges },
        str(args.description)
            ? { type: 'text', text: str(args.description) }
            : null,
        rows.length ? { type: 'table', rows } : null,
    ])
}

// A strategy recommendation (add_/update_strategy_recommendation). Each
// positioning axis gets its own colour; the size is surfaced as a reach badge.
export function strategyRecommendationToBlocks(
    rec: Record<string, unknown>,
    names: Record<string, string>
): { blocks: Block[] } {
    const r = rec ?? {}
    const badges: BadgeItem[] = []
    const add = (v: unknown, color: string) => {
        const label = humanize(v)
        if (label) badges.push({ label, color })
    }
    add(r.strategy_family, 'grape')
    add(r.funnel_stage, 'blue')
    add(r.target_action, 'teal')
    add(r.modeling_action, 'orange')
    const reach = formatReach(r.audience_size_estimate)
    if (reach) badges.push(reach)

    const datasetLabels = (Array.isArray(r.sources) ? r.sources : [])
        .map((s) =>
            datasetLabel((s as Record<string, unknown>)?.dataset_id, names)
        )
        .filter(Boolean)
    const relationship = (
        Array.isArray(r.audience_relationship) ? r.audience_relationship : []
    )
        .map(humanize)
        .filter(Boolean)
    const rows: TableRow[] = [
        ...(datasetLabels.length
            ? [{ label: 'Dataset', value: datasetLabels.join(' × ') }]
            : []),
        ...(relationship.length
            ? [{ label: 'Audience', value: relationship.join(', ') }]
            : []),
    ]

    const bullets: string[] = []
    const rationale = str(r.rationale)
    if (rationale) bullets.push(rationale)
    for (const w of Array.isArray(r.warnings) ? r.warnings : []) {
        const m = str((w as Record<string, unknown>)?.message)
        if (m) bullets.push(m)
    }

    return compact([
        str(r.name) ? { type: 'heading', text: str(r.name) } : null,
        badges.length ? { type: 'badges', items: badges } : null,
        str(r.description) ? { type: 'text', text: str(r.description) } : null,
        rows.length ? { type: 'table', rows } : null,
        bullets.length ? { type: 'list', items: bullets } : null,
    ])
}
